import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import { useCampaignConfig } from '@/hooks/use-campaign-config';
import {
  useIsUpdateCampaignConfigPending,
  useUpdateCampaignConfig,
} from '@/hooks/use-update-campaign-config';
import {
  campaignConfigSchema,
  toCampaignConfigFormValues,
  type CampaignConfigFormValues,
} from '@/lib/campaign/campaign-config';

export interface CampaignConfigDialogProps {
  campaignId: string;
  campaignOrganizationId: string;
  campaignDisplayName: string;
  onClose: () => void;
}

interface CampaignConfigFormProps extends CampaignConfigDialogProps {
  initialValues: CampaignConfigFormValues;
}

function CampaignConfigForm({
  campaignId,
  campaignOrganizationId,
  campaignDisplayName,
  initialValues,
  onClose,
}: CampaignConfigFormProps): JSX.Element {
  const updateCampaignConfig = useUpdateCampaignConfig();
  const isUpdatePending = useIsUpdateCampaignConfigPending();
  const form = useForm<CampaignConfigFormValues>({
    resolver: zodResolver(campaignConfigSchema),
    defaultValues: initialValues,
    mode: 'onSubmit',
  });
  const { dirtyFields, errors } = form.formState;

  const onSubmit = form.handleSubmit(async (formValues): Promise<void> => {
    const hasDirtyIdentity =
      dirtyFields.campaign_name === true || dirtyFields.product_line === true;

    if (!hasDirtyIdentity) {
      onClose();
      return;
    }

    try {
      await updateCampaignConfig.mutateAsync({
        campaignId,
        campaignOrganizationId,
        formValues,
        dirtyFields,
      });
      onClose();
    } catch {
      // The mutation owns the operator-facing error toast. Keep the dialog
      // open with the typed values intact so the operator can retry.
    }
  });

  return (
    // DIFF r1 F4: the fieldset is disabled while a save is in flight, not just the
    // submit button. The payload is built from `dirtyFields` at submit time, so an
    // edit typed after Save can never be part of the request already travelling —
    // and `onClose()` runs on success, taking those edits with it. Leaving the
    // inputs live advertises an edit the system has already decided to discard.
    // A failed request keeps the dialog open with the typed values intact, so the
    // fieldset re-enables with exactly what the operator last saw.
    <form onSubmit={onSubmit} noValidate className="space-y-6" aria-busy={isUpdatePending}>
      <fieldset
        className="space-y-4 rounded-lg border p-4 disabled:opacity-60"
        data-campaign-config-section="identity"
        disabled={isUpdatePending}
      >
        <legend className="px-1 text-base font-semibold">Identity</legend>

        <div className="space-y-2">
          <Label htmlFor="campaign-config-name">Campaign name</Label>
          <Input
            id="campaign-config-name"
            maxLength={200}
            placeholder={campaignDisplayName}
            className="min-h-11 md:min-h-11"
            data-content-origin="user"
            {...form.register('campaign_name')}
          />
          <p className="text-xs text-muted-foreground">
            Leave this empty to return the campaign to its generated name.
          </p>
          {errors.campaign_name && (
            <p className="text-sm text-destructive" role="alert">
              {errors.campaign_name.message}
            </p>
          )}
        </div>

        <div className="space-y-2">
          <Label htmlFor="campaign-config-product-line">Product line</Label>
          <Input
            id="campaign-config-product-line"
            maxLength={200}
            className="min-h-11 md:min-h-11"
            data-content-origin="user"
            {...form.register('product_line')}
          />
          {errors.product_line && (
            <p className="text-sm text-destructive" role="alert">
              {errors.product_line.message}
            </p>
          )}
        </div>
      </fieldset>

      <DialogFooter className="gap-2 sm:gap-0">
        <Button
          type="button"
          variant="outline"
          className="min-h-11 md:min-h-11"
          onClick={onClose}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          className="min-h-11 md:min-h-11"
          disabled={isUpdatePending}
        >
          {isUpdatePending ? 'Saving…' : 'Save settings'}
        </Button>
      </DialogFooter>
    </form>
  );
}

export function CampaignConfigDialog({
  campaignId,
  campaignOrganizationId,
  campaignDisplayName,
  onClose,
}: CampaignConfigDialogProps): JSX.Element {
  const configQuery = useCampaignConfig(campaignId);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-h-[85dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Campaign settings</DialogTitle>
          <DialogDescription>
            Set the identity operators see across the campaign workspace.
          </DialogDescription>
        </DialogHeader>

        {configQuery.isPending && (
          <div className="space-y-4" role="status" aria-label="Loading campaign settings">
            <Skeleton className="h-5 w-24" />
            <Skeleton className="h-11 w-full" />
            <Skeleton className="h-11 w-full" />
            <span className="sr-only">Loading campaign settings…</span>
          </div>
        )}

        {configQuery.isError && (
          <div className="space-y-4">
            <p className="text-sm text-destructive" role="alert">
              Campaign settings could not be loaded. Close the dialog and try again.
            </p>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                className="min-h-11 md:min-h-11"
                onClick={onClose}
              >
                Cancel
              </Button>
            </DialogFooter>
          </div>
        )}

        {configQuery.isSuccess && (
          <CampaignConfigForm
            campaignId={campaignId}
            campaignOrganizationId={campaignOrganizationId}
            campaignDisplayName={campaignDisplayName}
            initialValues={toCampaignConfigFormValues(configQuery.data)}
            onClose={onClose}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
